import { type ReactNode, useEffect, useRef } from "react";
import { Keyboard } from "react-native";
import { AnimatePresence, isWeb, styled, type TamaguiElement, usePresence, YStack } from "@medbillai/ui";
import { FlowTimelineGroupContents } from "./FlowTimelineGroupContents";
import { type FlowTimelineTransitionProps, groupFlowTimelineFrames } from "./timelineTransition";
const SLIDE_OFFSET = 48;
type Direction = 1 | -1;
type FramePosition = "past" | "active" | "future";

// The active frame sits in normal flow so an auto-height page layout grows
// with its content; inactive frames are absolute so they never affect layout.
const AnimatedFrame = styled(YStack, {
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: "100%",
  height: "100%",
  transition: {
    default: "enter",
    enter: "enter",
    exit: "exit",
    opacity: "fade"
  },
  animateOnly: ["opacity", "transform"],
  variants: {
    timelinePosition: {
      past: {
        position: "absolute",
        opacity: 0,
        x: -SLIDE_OFFSET
      },
      active: {
        position: "relative",
        opacity: 1,
        x: 0
      },
      future: {
        position: "absolute",
        opacity: 0,
        x: SLIDE_OFFSET
      }
    },
    transitionDirection: {
      ":number": (direction: number) => ({
        enterStyle: {
          opacity: 0,
          x: direction * SLIDE_OFFSET
        },
        exitStyle: {
          opacity: 0,
          x: -direction * SLIDE_OFFSET
        }
      })
    }
  } as const
});
function useInferredDirection(activeStateKey: string, activeIndex: number): Direction {
  const previousActiveRef = useRef({
    stateKey: activeStateKey,
    index: activeIndex,
    direction: 1 as Direction
  });
  const previousActive = previousActiveRef.current;
  const direction = previousActive.stateKey === activeStateKey ? previousActive.direction : activeIndex >= previousActive.index ? 1 : -1;
  useEffect(() => {
    previousActiveRef.current = {
      stateKey: activeStateKey,
      index: activeIndex,
      direction
    };
  }, [activeIndex, activeStateKey, direction]);
  return direction;
}
function TransitionFrame({
  position,
  direction,
  children
}: {
  position: FramePosition;
  direction: Direction;
  children: ReactNode;
}) {
  // AnimatePresence keeps a leaving frame rendered with its last props, so
  // presence, not position, says whether the member can still reach it.
  const [isPresent] = usePresence();
  const interactive = position === "active" && isPresent;

  // A frame that stops being interactive stays mounted, and hiding it from
  // accessibility does not release focus, so an input it owns would keep the
  // keyboard open over the arriving screen and keep taking keystrokes. On web
  // `aria-hidden` also leaves its controls in the Tab order, hence `inert`.
  const frameRef = useRef<TamaguiElement>(null);
  useEffect(() => {
    if (isWeb && frameRef.current instanceof HTMLElement) {
      frameRef.current.toggleAttribute("inert", !interactive);
    }
    if (!interactive) {
      return;
    }
    return () => Keyboard.dismiss();
  }, [interactive]);
  return <AnimatedFrame timelinePosition={position} transitionDirection={direction} pointerEvents={interactive ? "auto" : "none"} aria-hidden={!interactive} ref={frameRef} accessibilityElementsHidden={!interactive} importantForAccessibility={interactive ? "auto" : "no-hide-descendants"} data-sentry-element="AnimatedFrame" data-sentry-component="TransitionFrame" data-sentry-source-file="FlowTimelineTamaguiTransition.tsx">
      {children}
    </AnimatedFrame>;
}
export function FlowTimelineTamaguiTransition({
  frames,
  activeStateKey
}: FlowTimelineTransitionProps) {
  const groups = groupFlowTimelineFrames(frames);
  const activeIndex = groups.findIndex(group => group.visits.some(visit => visit.stateKey === activeStateKey));
  if (activeIndex < 0) {
    throw new Error("FlowTimelineTamaguiTransition: active group is missing");
  }
  const activeGroupStateKey = groups[activeIndex]?.stateKey;
  if (!activeGroupStateKey) {
    throw new Error("FlowTimelineTamaguiTransition: active group is missing");
  }
  const direction = useInferredDirection(activeGroupStateKey, activeIndex);
  return <AnimatePresence initial={false} custom={{
    transitionDirection: direction
  }} presenceAffectsLayout={false} data-sentry-element="AnimatePresence" data-sentry-component="FlowTimelineTamaguiTransition" data-sentry-source-file="FlowTimelineTamaguiTransition.tsx">
      {groups.map((group, index) => <TransitionFrame key={group.stateKey} position={index < activeIndex ? "past" : index > activeIndex ? "future" : "active"} direction={direction}>
          <FlowTimelineGroupContents group={group} activeStateKey={activeStateKey} />
        </TransitionFrame>)}
    </AnimatePresence>;
}